import { useEffect, useRef, useState } from "react";
import { getSessionTicket, uploadPhoto } from "../api.js";
import { processImage } from "../utils/image.js";

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve();
  if (document.querySelector(`script[src="${TURNSTILE_SRC}"]`)) {
    return new Promise((resolve) => {
      const t = setInterval(() => {
        if (window.turnstile) {
          clearInterval(t);
          resolve();
        }
      }, 50);
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function Upload({ activity, open, onClose, onUploaded }) {
  const [uploader, setUploader] = useState(() => localStorage.getItem("e118.uploader") || "");
  const [files, setFiles] = useState([]); // [{ file, status, progress, error, photoId }]
  const [running, setRunning] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  const dropRef = useRef(null);

  // 載入 + render Turnstile widget
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadTurnstile().then(() => {
      if (cancelled || !widgetRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(null),
        "error-callback": () => setTurnstileToken(null),
        theme: "light",
        appearance: "always",
      });
    });
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
      }
      widgetIdRef.current = null;
      setTurnstileToken(null);
    };
  }, [open]);

  // 拖放
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = (e) => {
      prevent(e);
      addFiles(Array.from(e.dataTransfer.files || []));
    };
    el.addEventListener("dragenter", prevent);
    el.addEventListener("dragover", prevent);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragenter", prevent);
      el.removeEventListener("dragover", prevent);
      el.removeEventListener("drop", onDrop);
    };
  }, [open]);

  function addFiles(list) {
    const accept = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
    const ok = list.filter((f) => accept.includes((f.type || "").toLowerCase()));
    setFiles((prev) => [
      ...prev,
      ...ok.map((file) => ({ file, status: "pending", progress: 0, error: null, photoId: null })),
    ]);
  }

  async function startUpload() {
    if (running) return;

    // 從 widget 現場拿最新 token（不靠 React state）
    let liveToken = turnstileToken;
    if (window.turnstile && widgetIdRef.current) {
      try {
        const fresh = window.turnstile.getResponse(widgetIdRef.current);
        if (fresh) liveToken = fresh;
      } catch {}
    }
    if (!liveToken) { alert("請先完成上方驗證"); return; }
    if (uploader.trim()) localStorage.setItem("e118.uploader", uploader.trim());
    setRunning(true);

    // 一次驗 Turnstile，換 10 分鐘 ticket，後續所有上傳都用 ticket
    let session;
    try {
      session = await getSessionTicket(liveToken);
    } catch (err) {
      alert(`驗證失敗：${err.message}\n請按 Cancel 重新整理頁面再試`);
      setRunning(false);
      setTurnstileToken(null);
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.reset(widgetIdRef.current); } catch {}
      }
      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (files[i].status === "done") continue;

      // 1) 壓縮
      setFile(i, { status: "compressing", progress: 0 });
      let processed;
      try {
        processed = await processImage(files[i].file);
      } catch (err) {
        setFile(i, { status: "error", error: `壓縮失敗：${err.message}` });
        continue;
      }

      // 2) 上傳（用 session ticket，不需再驗 Turnstile）
      setFile(i, { status: "uploading", progress: 50 });
      try {
        const result = await uploadPhoto({
          activityId: activity.id,
          uploader: uploader.trim() || null,
          exifDate: processed.exifDate,
          thumb: processed.thumb,
          medium: processed.medium,
          original: processed.original,
          sessionTicket: session.ticket,
        });
        setFile(i, { status: "done", progress: 100, photoId: result.photoId });
      } catch (err) {
        setFile(i, { status: "error", error: err.message });
        // ticket 失效（10 分鐘到了或被 reject）→ 整個 batch 中止
        if (String(err.message).includes("session_invalid_or_expired")) {
          break;
        }
      }
    }

    setRunning(false);
    onUploaded?.();
  }

  function setFile(i, patch) {
    setFiles((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }

  if (!open) return null;

  const allDone = files.length > 0 && files.every((f) => f.status === "done");
  const hasFiles = files.length > 0;

  return (
    <div className="upload-overlay" onClick={onClose}>
      <div className="upload-panel" onClick={(e) => e.stopPropagation()}>
        <header>
          <h3>上傳照片到 「{activity.title}」</h3>
          <button className="upload-close" onClick={onClose} aria-label="關閉">×</button>
        </header>

        <label className="upload-name">
          <span>你的名字（可不填，會顯示「E118 同學」）</span>
          <input
            type="text"
            value={uploader}
            onChange={(e) => setUploader(e.target.value)}
            maxLength={50}
            placeholder="E118 同學"
          />
        </label>

        <div className="upload-turnstile" ref={widgetRef}></div>

        <div className="upload-drop" ref={dropRef}>
          <p>把照片拖到這裡，或</p>
          <label className="upload-pick">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => addFiles(Array.from(e.target.files || []))}
            />
            選擇檔案
          </label>
          <p className="upload-hint">支援 JPG / PNG / WebP / HEIC，單張 30 MB 以內</p>
        </div>

        {hasFiles && (
          <ul className="upload-list">
            {files.map((f, i) => (
              <li key={i} className={`upload-item upload-${f.status}`}>
                <span className="upload-name-cell">{f.file.name}</span>
                <span className="upload-status">
                  {f.status === "pending" && "待處理"}
                  {f.status === "compressing" && "壓縮中…"}
                  {f.status === "uploading" && "上傳中…"}
                  {f.status === "done" && "✓ 完成"}
                  {f.status === "error" && `✗ ${f.error}`}
                </span>
              </li>
            ))}
          </ul>
        )}

        <footer>
          {allDone ? (
            <button className="upload-go upload-done" onClick={onClose}>完成 — 關閉</button>
          ) : (
            <button
              className="upload-go"
              onClick={startUpload}
              disabled={running || !hasFiles || !turnstileToken}
            >
              {running ? "上傳中…" : `開始上傳 (${files.length} 張)`}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
